import { useRef, useState, useEffect } from 'react';
import {
  CameraIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/20/solid';
import { ReactComponent as KebabSvg } from '../../../../assets/svg/kebab.svg';
import { ReactComponent as CloseSvg } from '../../../../assets/svg/closeCircle.svg';
import { ReactComponent as PhotoSvg } from '../../../../assets/svg/img.svg';
import { formatDate, resizeImgFile } from '../../../../lib/utils';
import request from '../../../../lib/request';
import Title from '../../../../components/dialog/title';
import axios from 'axios';

const ANGLE = {
  FRONT: 'front',
  BACK: 'back',
  SIDE: 'side',
};

const AddPhoto = ({ source, fileRef, handleCapture, handleClickCapture }) => {
  const inputId = Math.random().toString();
  return (
    <div
      className={`relative flex justify-center aspect-square rounded-xl overflow-hidden w-full h-full`}
    >
      {source ? (
        <div className='w-full h-full'>
          <img
            src={source}
            alt='source'
            className='w-full h-full object-cover'
          />
        </div>
      ) : (
        <div className='flex flex-col justify-center items-center w-full h-full'>
          <input
            ref={fileRef}
            accept='image/*'
            id={inputId}
            type='file'
            onChange={handleCapture}
            onClick={handleClickCapture}
            className='hidden'
          />
          <label
            htmlFor={inputId}
            className='flex flex-col justify-center items-center w-full h-full text-white opacity-40'
          >
            <CameraIcon className='size-8' />
            <div className='text-xs'>Click to add</div>
          </label>
        </div>
      )}
    </div>
  );
};

const ViewLog = ({ viewDialogRef, closeDialog, log, refresh }) => {
  const bottomDialogRef = useRef(null);
  const fileRef = useRef(null);

  const frontFileRef = useRef(null);
  const sideFileRef = useRef(null);
  const backFileRef = useRef(null);

  const [currentAngle, setCurrentAngle] = useState(ANGLE.FRONT);
  const [loading, setLoading] = useState(false);
  const [currentLog, setCurrentLog] = useState(null);

  useEffect(() => {
    if (log?.id) {
      getBodyLog(log.id);
    }
  }, [log]);

  const getBodyLog = async (id) => {
    try {
      const { result } = await request.get(`/log/body/${id}`);

      if (result) {
        setCurrentLog(result);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleClose = () => {
    closeDialog();
    setCurrentLog(null);
    refresh();
  };

  const handleRemoveCurrentPhoto = async () => {
    try {
      if (
        !window.confirm(
          `Are you sure you want to remove the ${currentAngle} photo?`
        )
      ) {
        return;
      }

      setLoading(true);

      const { result } = await request.delete(
        `/log/body/${currentLog.id}?angle=${currentAngle}`
      );
      if (!result) {
        return alert('Failed to remove.');
      }

      closeBottomDialog();
      await getBodyLog(currentLog.id);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (!window.confirm('Are you sure you want to delete this item?')) {
        return;
      }

      setLoading(true);

      const { result } = await request.delete(`/log/body/${currentLog.id}`);
      if (!result) {
        return alert('Failed to delete.');
      }

      closeBottomDialog();
      handleClose();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const openBottomDialog = (angle) => () => {
    if (bottomDialogRef.current) {
      setCurrentAngle(angle);
      bottomDialogRef.current.showModal();
    }
  };

  const closeBottomDialog = () => {
    if (bottomDialogRef.current) {
      bottomDialogRef.current.close();
    }
  };

  const uploadFile = async (file) => {
    const { result } = await request.get(
      `/upload?filename=${file.name}&contentType=${file.type}&type=body`
    );

    if (!result?.url || !result?.key) {
      throw new Error('Failed to upload');
    }

    await axios.put(result.url, file, {
      headers: {
        'Content-Type': file.type,
      },
    });

    return result;
  };

  const handleClickCapture = (angle) => () => {
    if (angle === ANGLE.FRONT) {
      if (frontFileRef.current && frontFileRef.current.value) {
        frontFileRef.current.value = '';
      }
    }
    if (angle === ANGLE.SIDE) {
      if (sideFileRef.current && sideFileRef.current.value) {
        sideFileRef.current.value = '';
      }
    }
    if (angle === ANGLE.BACK) {
      if (backFileRef.current && backFileRef.current.value) {
        backFileRef.current.value = '';
      }
    }
  };

  const handleCapture = (angle) => async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      let resizedFile = await resizeImgFile(file);

      if (file.size < resizedFile.size) {
        resizedFile = file;
      }

      const uploadedFile = await uploadFile(resizedFile);

      try {
        if (angle === ANGLE.FRONT) {
          await request.put(`/log/body/${currentLog.id}`, {
            frontPhotoKey: uploadedFile.key,
          });
        }
        if (angle === ANGLE.SIDE) {
          await request.put(`/log/body/${currentLog.id}`, {
            sidePhotoKey: uploadedFile.key,
          });
        }
        if (angle === ANGLE.BACK) {
          await request.put(`/log/body/${currentLog.id}`, {
            backPhotoKey: uploadedFile.key,
          });
        }

        await getBodyLog(currentLog.id);
        closeBottomDialog();
      } catch (err) {
        console.log(err);
      }
    }
  };

  const handleSelectPhoto = (angle) => () => {
    if (angle === ANGLE.FRONT) {
      setCurrentAngle(ANGLE.FRONT);
    }
    if (angle === ANGLE.SIDE) {
      setCurrentAngle(ANGLE.SIDE);
    }
    if (angle === ANGLE.BACK) {
      setCurrentAngle(ANGLE.BACK);
    }
  };

  return (
    <dialog
      ref={viewDialogRef}
      className='w-full h-full max-w-lg max-h-full p-4 relative text-white bg-secondary scrollbar-hidden'
    >
      <div className='flex items-center justify-between'>
        <Title
          title={`Log on ${formatDate(
            new Date(currentLog ? currentLog.logDate : undefined)
          )}`}
          onClick={handleClose}
          color='white'
        />
      </div>

      {/* Full view */}
      <div className='w-full overflow-hidden rounded-xl mb-4 border border-tertiary'>
        {currentAngle === ANGLE.FRONT ? (
          currentLog?.frontPhotoUrl ? (
            <div className='relative w-full h-full'>
              <img
                src={currentLog.frontPhotoUrl}
                alt='front'
                className='w-full h-full object-cover'
              />
              <button
                className='absolute top-3 right-2 bg-tertiary/50 rounded-full p-1'
                onClick={openBottomDialog(ANGLE.FRONT)}
              >
                <KebabSvg className='size-5' />
              </button>
            </div>
          ) : (
            <AddPhoto
              source={currentLog?.frontPhotoUrl}
              fileRef={frontFileRef}
              handleCapture={handleCapture(ANGLE.FRONT)}
              handleClickCapture={handleClickCapture(ANGLE.FRONT)}
            />
          )
        ) : null}
        {currentAngle === ANGLE.SIDE ? (
          currentLog?.sidePhotoUrl ? (
            <div className='relative w-full h-full'>
              <img
                src={currentLog.sidePhotoUrl}
                alt='side'
                className='w-full h-full object-cover'
              />
              <button
                className='absolute top-3 right-2 bg-tertiary/50 rounded-full p-1'
                onClick={openBottomDialog(ANGLE.SIDE)}
              >
                <KebabSvg className='size-5' />
              </button>
            </div>
          ) : (
            <AddPhoto
              source={currentLog?.sidePhotoUrl}
              fileRef={sideFileRef}
              handleCapture={handleCapture(ANGLE.SIDE)}
              handleClickCapture={handleClickCapture(ANGLE.SIDE)}
            />
          )
        ) : null}
        {currentAngle === ANGLE.BACK ? (
          currentLog?.backPhotoUrl ? (
            <div className='relative w-full h-full'>
              <img
                src={currentLog.backPhotoUrl}
                alt='back'
                className='w-full h-full object-cover'
              />
              <button
                className='absolute top-3 right-2 bg-tertiary/50 rounded-full p-1'
                onClick={openBottomDialog(ANGLE.BACK)}
              >
                <KebabSvg className='size-5' />
              </button>
            </div>
          ) : (
            <AddPhoto
              source={currentLog?.backPhotoUrl}
              fileRef={backFileRef}
              handleCapture={handleCapture(ANGLE.BACK)}
              handleClickCapture={handleClickCapture(ANGLE.BACK)}
            />
          )
        ) : null}
      </div>

      {/* Thumbnail view */}
      <div className='grid grid-cols-3 gap-4 mb-4'>
        <div
          className='flex flex-col justify-center items-center gap-2'
          onClick={handleSelectPhoto(ANGLE.FRONT)}
        >
          <div
            className={`rounded-xl aspect-square overflow-hidden w-full h-full ${
              currentAngle === ANGLE.FRONT ? 'border-2 border-accent' : ''
            }`}
          >
            {currentLog?.frontPhotoUrl ? (
              <img
                src={currentLog.frontPhotoUrl}
                alt='front'
                className='w-full h-full object-cover'
              />
            ) : (
              <div className='flex justify-center items-center w-full h-full rounded-xl border border-tertiary'>
                <PhotoSvg
                  className='size-8 opacity-20'
                  fill='white'
                />
              </div>
            )}
          </div>
          <label
            className={`text-center text-xs ${
              currentAngle === ANGLE.FRONT
                ? 'text-accent font-bold'
                : 'text-white/50'
            }`}
          >
            Front
          </label>
        </div>
        <div
          className='flex flex-col justify-center items-center gap-2'
          onClick={handleSelectPhoto(ANGLE.SIDE)}
        >
          <div
            className={`rounded-xl aspect-square overflow-hidden w-full h-full ${
              currentAngle === ANGLE.SIDE ? 'border-2 border-accent' : ''
            }`}
          >
            {currentLog?.sidePhotoUrl ? (
              <img
                src={currentLog.sidePhotoUrl}
                alt='side'
                className='w-full h-full object-cover'
              />
            ) : (
              <div className='flex justify-center items-center w-full h-full rounded-xl border border-tertiary'>
                <PhotoSvg
                  className='size-8 opacity-20'
                  fill='white'
                />
              </div>
            )}
          </div>
          <label
            className={`text-center text-xs ${
              currentAngle === ANGLE.SIDE
                ? 'text-accent font-bold'
                : 'text-white/50'
            }`}
          >
            Side
          </label>
        </div>
        <div
          className='flex flex-col justify-center items-center gap-2'
          onClick={handleSelectPhoto(ANGLE.BACK)}
        >
          <div
            className={`rounded-xl aspect-square overflow-hidden w-full h-full ${
              currentAngle === ANGLE.BACK ? 'border-2 border-accent' : ''
            }`}
          >
            {currentLog?.backPhotoUrl ? (
              <img
                src={currentLog.backPhotoUrl}
                alt='back'
                className='w-full h-full object-cover'
              />
            ) : (
              <div className='flex justify-center items-center w-full h-full rounded-xl border border-tertiary'>
                <PhotoSvg
                  className='size-8 opacity-20'
                  fill='white'
                />
              </div>
            )}
          </div>
          <label
            className={`text-center text-xs ${
              currentAngle === ANGLE.BACK
                ? 'text-accent font-bold'
                : 'text-white/50'
            }`}
          >
            Back
          </label>
        </div>
      </div>

      {currentLog?.note && (
        <div className='text-sm'>
          <div className='font-bold mb-2'>Note</div>
          <p className='bg-tertiary rounded-xl p-2 break-all '>
            {currentLog?.note}
          </p>
        </div>
      )}

      <dialog
        ref={bottomDialogRef}
        className='w-full h-full max-w-lg max-h-full bg-transparent'
        onCancel={closeBottomDialog}
      >
        <div
          className={`bg-secondary fixed bottom-0 w-full max-w-lg rounded-t-3xl px-3 py-6 text-white`}
        >
          <div className='mb-4 flex justify-end'>
            <CloseSvg
              className='size-5'
              onClick={closeBottomDialog}
            />
          </div>
          <div className='text-sm space-y-4'>
            <div>
              <input
                ref={fileRef}
                accept='image/*'
                id={currentAngle}
                type='file'
                onChange={handleCapture(currentAngle)}
                onClick={handleClickCapture(currentAngle)}
                className='hidden'
                disabled={loading}
              />
              <label htmlFor={currentAngle}>
                <div className='px-3 py-2 flex items-center gap-2'>
                  <PencilSquareIcon className='size-5 opacity-40' />
                  <span>
                    Change <span className='text-accent'>{currentAngle}</span>{' '}
                    photo
                  </span>
                </div>
              </label>
            </div>
            <button
              className='px-3 py-2 flex items-center gap-2'
              onClick={handleRemoveCurrentPhoto}
              disabled={loading}
            >
              <TrashIcon className='size-5 opacity-40' />
              <span>
                Remove <span className='text-accent'>{currentAngle}</span> photo
              </span>
            </button>
            <button
              className='pl-3 pt-2 text-red-600 inline-block text-xs'
              onClick={handleDelete}
              disabled={loading}
            >
              Delete{' '}
              <span className='font-semibold'>
                {formatDate(
                  new Date(currentLog ? currentLog.logDate : undefined)
                )}
              </span>{' '}
              log
            </button>
          </div>
        </div>
      </dialog>
    </dialog>
  );
};

export default ViewLog;
